import React, { FC, useEffect, useState } from "react";
import { Box, Link, Snackbar, Stack, SxProps, Typography } from "@mui/material";

import XIcon from "../Icons/x";
import LinkedInIcon from "../Icons/linkedin";
import FacebookIcon from "../Icons/facebook";
import { Colors } from "@theme/colors";
import EmailIcon from "@mui/icons-material/Email";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import { uiContainerTransitionMs, uiOpacityHover } from "@src/utils";
import { addParameters } from "@src/urls";
import { UTM_FIELD_KEYS } from "@src/helpers/utms";

interface Props {
  pathToShare: string;
  titleToShare: string;
  blurbToShare: string;
  utmCampaign?: string;

  articleTitleTemplate?: string; // The text which wraps the title for some sharing. Use `<title>` (without quotes) to interpolate the title variable
  mailtoBodyTemplate?: string; // The mailto body text for sharing via email. Use `<title>`, `<blurb>` and `<url>` (without quotes) to interpolate the respective variables
  shareTypeText?: string; // Name of the resource being shared, lowercase (example: 'article', 'blog post', 'banana')
  buttonStyles?: SxProps;
  containerStyles?: SxProps;
}

const defaultMailtoBodyTemplate = `Please check out this security guide from the Secure by Design Education Hub: <url>

<title>: <blurb>
`;

const defaultArticleTitleTemplate = `Shared article from Pangea: <title>`;

const ShareButtons: FC<Props> = ({
  pathToShare = "",
  titleToShare = "",
  blurbToShare = "",
  utmCampaign = "",
  mailtoBodyTemplate = defaultMailtoBodyTemplate,
  articleTitleTemplate = defaultArticleTitleTemplate,
  shareTypeText = "article",
  buttonStyles = {},
  containerStyles = {},
}) => {
  if (
    pathToShare?.trim() === "" ||
    titleToShare.trim() === "" ||
    blurbToShare.trim() === ""
  ) {
    return;
  }

  const [calculatedUrl, setCalculatedUrl] = useState<string>("");
  const [calculatedText, setCalculatedText] = useState<string>("");
  const [calculatedMailtoBody, setCalculatedMailtoBody] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarDebounce, setSnackbarDebounce] = useState<any>();

  useEffect(() => {
    let url = addParameters(
      `${process.env.GATSBY_DOCS_BASE_URL}/${pathToShare}${
        pathToShare.charAt(pathToShare.length - 1) !== "/" ? "/" : ""
      }`,
      {
        [UTM_FIELD_KEYS.UTM_CAMPAIGN]: utmCampaign,
      },
    );

    setCalculatedUrl(url);

    const titleText = articleTitleTemplate.replace("<title>", titleToShare);

    setCalculatedText(titleText);

    let mailtoBody = mailtoBodyTemplate.replace("<url>", url);
    mailtoBody = mailtoBody.replace("<title>", titleToShare);
    mailtoBody = mailtoBody.replace("<blurb>", blurbToShare);

    setCalculatedMailtoBody(mailtoBody);
  }, []);

  const color = Colors.PrimaryContrast;
  const size = "16px";

  const styles: SxProps = {
    a: {
      backgroundColor: Colors.PrimaryMain,
      width: "40px",
      height: "40px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50px",
      opacity: 1,
      transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
      svg: {
        width: "20px",
        height: "20px",
      },
      "&:hover": {
        opacity: uiOpacityHover,
      },
      ...(buttonStyles ?? {}),
    },
    ...(containerStyles ?? {}),
  } as SxProps;

  return (
    <Stack spacing={2}>
      {shareTypeText?.trim() !== "" ? (
        <Typography
          variant="body2"
          sx={{
            fontSize: "18px",
            lineHeight: "24px",
            fontWeight: "400",
            color: Colors.TextPrimary,
          }}
        >
          Share this {shareTypeText}
        </Typography>
      ) : (
        <Box>&nbsp;</Box>
      )}

      <Stack direction="row" spacing={"10px"} sx={styles}>
        <Link
          sx={{
            ":hover": {
              cursor: "pointer",
            },
          }}
          title="Copy link"
          data-qa={`share-button-copy-${pathToShare}`}
          onClick={(e) => {
            e.preventDefault();

            navigator.clipboard.writeText(calculatedUrl);

            if (snackbarOpen) {
              setSnackbarOpen(false);

              if (snackbarDebounce) {
                clearTimeout(snackbarDebounce);
                setSnackbarDebounce(null);
              }

              setSnackbarDebounce(
                setTimeout(() => {
                  setSnackbarOpen(true);
                }, 100),
              );
            }

            setSnackbarOpen(true);
          }}
        >
          <InsertLinkIcon
            sx={{
              color: Colors.PrimaryContrast,
            }}
          />
        </Link>
        <Link
          href={`mailto:?subject=${encodeURIComponent(
            calculatedText,
          )}&body=${encodeURIComponent(calculatedMailtoBody)}`}
          target="_blank"
          rel="noreferrer"
          data-qa={`share-button-mailto-${pathToShare}`}
        >
          <EmailIcon
            sx={{
              color: Colors.PrimaryContrast,
            }}
          />
        </Link>
        <Link
          href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
            addParameters(calculatedUrl, {
              [UTM_FIELD_KEYS.UTM_SOURCE]: "linkedin",
              [UTM_FIELD_KEYS.UTM_MEDIUM]: "social",
            }),
          )}`}
          target="_blank"
          rel="noreferrer"
          data-qa={`share-button-linkedin-${pathToShare}`}
        >
          <LinkedInIcon color={color} size={size} />
        </Link>
        <Link
          href={`https://x.com/intent/tweet?text=${encodeURIComponent(
            calculatedText,
          )}&url=${encodeURIComponent(
            addParameters(calculatedUrl, {
              [UTM_FIELD_KEYS.UTM_SOURCE]: "x",
              [UTM_FIELD_KEYS.UTM_MEDIUM]: "social",
            }),
          )}`}
          target="_blank"
          rel="noreferrer"
          data-qa={`share-button-x-${pathToShare}`}
        >
          <XIcon color={color} size={size} />
        </Link>
        <Link
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            addParameters(calculatedUrl, {
              [UTM_FIELD_KEYS.UTM_SOURCE]: "facebook",
              [UTM_FIELD_KEYS.UTM_MEDIUM]: "social",
            }),
          )}`}
          target="_blank"
          rel="noreferrer"
          data-qa={`share-button-facebook-${pathToShare}`}
        >
          <FacebookIcon color={color} size={size} />
        </Link>

        <Snackbar
          open={snackbarOpen}
          onClose={() => {
            setSnackbarOpen(false);
          }}
          autoHideDuration={3000}
          message="Link copied"
        />
      </Stack>
    </Stack>
  );
};

export default ShareButtons;
